import React, { useState, useEffect } from "react";
import { jsPDF } from "jspdf";
import useAtendimento from "../../hooks/useAtendimento";
import { formatarNomeUpper } from "../../utils/formatadores"

const ModalProntuarioCliente = ({ onClose, cliente, atendimentos = [] }) => {
  const [listaAtendimentos, setListaAtendimentos] = useState([]);

  // Consome o hook existente
  const { buscarAtendimentosPorCliente, carregando } = useAtendimento();

  // Extrai o ID do cliente de forma segura
  const clienteId = cliente?._id || cliente?.id;
  const quantidadeAtendimentosProps = atendimentos?.length || 0;

  // 1. Sincroniza as PROPS 'atendimentos'
  useEffect(() => {
    if (quantidadeAtendimentosProps > 0) {
      setListaAtendimentos(atendimentos);
    }
  }, [quantidadeAtendimentosProps, atendimentos]);

  // 2. Busca no BACKEND via Custom Hook caso props venham vazias
  useEffect(() => {
    let cancelado = false;

    if (quantidadeAtendimentosProps === 0 && clienteId) {
      buscarAtendimentosPorCliente(clienteId)
        .then((res) => {
          if (!cancelado) {
            setListaAtendimentos(res?.data || res || []);
          }
        })
        .catch((err) => console.error("Erro ao carregar histórico:", err));
    }

    return () => {
      cancelado = true;
    };
  }, [clienteId, quantidadeAtendimentosProps, buscarAtendimentosPorCliente]);

  // Filtragem e Ordenação dos registros (Mais recente -> Mais antigo)
  const atendimentosFinalizados = (listaAtendimentos || [])
    .filter((item) => {
      const status = (item.status || "").toLowerCase().trim();
      const eFinalizado =
        status === "finalizado" || status === "realizado" || status === "concluído";

      let idDoClienteNoAtendimento = null;
      if (item.cliente) {
        if (typeof item.cliente === "object") {
          idDoClienteNoAtendimento = item.cliente._id || item.cliente.id;
        } else {
          idDoClienteNoAtendimento = item.cliente;
        }
      }

      const mesmoCliente =
        clienteId && idDoClienteNoAtendimento
          ? String(idDoClienteNoAtendimento) === String(clienteId)
          : true;

      return eFinalizado && mesmoCliente;
    })
    .sort((a, b) => {
      const dataA = new Date(a.updatedAt || a.createdAt || 0);
      const dataB = new Date(b.updatedAt || b.createdAt || 0);
      return dataB - dataA;
    });

  // Desenha o cabeçalho do escritório no PDF
  const desenharCabecalhoPDF = (doc) => {
    const corDestaque = [128, 40, 40];

    doc.setFont("times", "bold");
    doc.setFontSize(13);
    doc.setTextColor(0, 0, 0);
    doc.text("Rayane Cristina", 135, 20, { align: "right" });

    doc.setFont("times", "normal");
    doc.setFontSize(10);
    doc.setTextColor(60, 60, 60);
    doc.text("Rodrigues Pessoa Gomes", 135, 25, { align: "right" });

    doc.setDrawColor(...corDestaque);
    doc.setLineWidth(0.8);
    doc.line(140, 15, 140, 30);

    doc.setFont("times", "bold");
    doc.setFontSize(9);
    doc.setTextColor(...corDestaque);
    doc.text("ADVOGADA", 143, 19);
    doc.text("OAB/PA  27.956", 143, 24);
    doc.text("OAB/MA 23.002-A", 143, 29);

    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(1.2);
    doc.line(20, 34, 190, 34);
  };

  // Adiciona a numeração de página centralizada no rodapé
  const adicionarRodapeComPaginacao = (doc) => {
    const totalPaginas = doc.getNumberOfPages();

    for (let i = 1; i <= totalPaginas; i++) {
      doc.setPage(i);
      doc.setFont("times", "normal");
      doc.setFontSize(9);
      doc.setTextColor(100, 100, 100);
      doc.text(`Página ${i} de ${totalPaginas}`, 105, 287, { align: "center" });
    }
  };

  // 1. GERAR PDF INDIVIDUAL
  const gerarPDF = (atendimento) => {
    if (!atendimento) return;

    const doc = new jsPDF();
    const clienteData = atendimento.cliente || cliente;
    const advogado = atendimento.advogado;

    desenharCabecalhoPDF(doc);

    doc.setFont("times", "bold");
    doc.setFontSize(14);
    doc.setTextColor(0, 0, 0);
    doc.text("FICHA DE ATENDIMENTO", 105, 45, { align: "center" });

    doc.setFontSize(11);
    const nomeClienteStr = clienteData?.nomeCliente || clienteData?.nome || "N/A";
    const cpfClienteStr = clienteData?.cpf ? `CPF: ${clienteData.cpf} - ` : "";
    
    doc.text(`Cliente: ${formatarNomeUpper(nomeClienteStr)} - ${cpfClienteStr}`, 20, 57);

    doc.setFont("times", "normal");
    const nomeAdvogado =
      advogado?.nome || (typeof advogado === "string" ? advogado : "Não informado");
    doc.text(`Atendido por: ${formatarNomeUpper(nomeAdvogado)}`, 20, 64);

    const dataAtendimento = atendimento.updatedAt
      ? new Date(atendimento.updatedAt).toLocaleDateString("pt-BR")
      : new Date().toLocaleDateString("pt-BR");
    doc.text(`Data: ${dataAtendimento}`, 20, 71);

    doc.setFont("times", "bold");
    doc.text("Assunto:", 20, 83);
    doc.setFont("times", "normal");
    doc.text(atendimento.assunto || "Não informado", 40, 83);

    doc.setFont("times", "bold");
    doc.text("Descrição do Atendimento:", 20, 95);

    doc.setFont("times", "normal");
    const descricaoText = atendimento.descricao || "Sem descrição registrada.";

    const linhasDescricao = doc.splitTextToSize(descricaoText, 170);
    doc.text(linhasDescricao, 20, 102);

    // Insere a numeração de páginas em todas as folhas geradas
    adicionarRodapeComPaginacao(doc);

    const nomeArquivo = nomeClienteStr.replace(/[^a-zA-Z0-9]/g, "_");
    doc.save(`Ficha_${nomeArquivo}.pdf`);
  };

  // 2. GERAR PDF COMPLETO DO PRONTUÁRIO
  const gerarPDFProntuarioCompleto = () => {
    if (atendimentosFinalizados.length === 0) return;

    const doc = new jsPDF();
    const nomeClienteStr = cliente?.nomeCliente || cliente?.nome || "Cliente";
    const cpfClienteStr = cliente?.cpf ? `CPF: ${cliente.cpf} - ` : "";

    desenharCabecalhoPDF(doc);

    doc.setFont("times", "bold");
    doc.setFontSize(14);
    doc.setTextColor(0, 0, 0);
    doc.text("PRONTUÁRIO COMPLETO DE ATENDIMENTOS", 105, 45, { align: "center" });

    doc.setFontSize(11);
    doc.text(`Cliente: ${formatarNomeUpper(nomeClienteStr)} - ${cpfClienteStr}`, 20, 55);

    let y = 65;
    const limiteY = 270; // Limite inferior da folha A4 antes da margem do rodapé

    atendimentosFinalizados.forEach((item, index) => {
      const nomeAdvogado =
        formatarNomeUpper(item.advogado?.nome) ||
        (typeof item.advogado === "string" ? item.advogado : "Não informado");

      const dataFormatada = item.updatedAt || item.createdAt
        ? new Date(item.updatedAt || item.createdAt).toLocaleString("pt-BR")
        : "Data não registrada";

      const descricaoText = item.descricao || "Sem descrição registrada.";
      const linhasDescricao = doc.splitTextToSize(descricaoText, 170);
      const alturaEstimada = 35 + linhasDescricao.length * 5;

      // Se não couber na página atual, adiciona nova página
      if (y + alturaEstimada > limiteY) {
        doc.addPage();
        desenharCabecalhoPDF(doc);
        y = 45;
      }

      // Linha divisória de cada atendimento
      doc.setDrawColor(128, 40, 40);
      doc.setLineWidth(0.5);
      doc.line(20, y, 190, y);
      y += 6;

      // Assunto e Data
      doc.setFont("times", "bold");
      doc.setFontSize(11);
      doc.setTextColor(128, 40, 40);
      doc.text(`ATENDIMENTO #${atendimentosFinalizados.length - index}`, 20, y);

      doc.setFont("times", "normal");
      doc.setFontSize(10);
      doc.setTextColor(80, 80, 80);
      doc.text(`Data: ${dataFormatada}`, 190, y, { align: "right" });
      y += 6;

      doc.setFont("times", "bold");
      doc.setTextColor(0, 0, 0);
      doc.text("Assunto:", 20, y);
      doc.setFont("times", "normal");
      doc.text(item.assunto || "Não informado", 40, y);
      y += 6;

      doc.setFont("times", "normal");
      doc.setTextColor(80, 80, 80);
      doc.text(`Atendido por: ${nomeAdvogado}`, 20, y);
      y += 6;

      doc.setFont("times", "bold");
      doc.setTextColor(0, 0, 0);
      doc.text("Descrição:", 20, y);
      y += 5;

      doc.setFont("times", "normal");
      doc.text(linhasDescricao, 20, y);

      y += linhasDescricao.length * 5 + 10;
    });

    // Insere a numeração de páginas em todas as folhas geradas
    adicionarRodapeComPaginacao(doc);

    const nomeArquivo = nomeClienteStr.replace(/[^a-zA-Z0-9]/g, "_");
    doc.save(`Prontuario_Completo_${nomeArquivo}.pdf`);
  };

  // Formatação do CPF para exibição na tela do modal
  const cpfExibicao = cliente?.cpf ? ` - CPF: ${cliente.cpf}` : "";

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
    >
      <div
        style={{
          backgroundColor: "#fff",
          width: "90%",
          maxWidth: "800px",
          maxHeight: "90vh",
          overflowY: "auto",
          borderRadius: "8px",
          padding: "24px",
          boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
        }}
      >
        {/* CABEÇALHO DO PRONTUÁRIO */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            borderBottom: "2px solid #802828",
            paddingBottom: "12px",
            marginBottom: "20px",
          }}
        >
          <div>
            <h2 style={{ margin: 0, fontSize: "20px", color: "#333" }}>
              PRONTUÁRIO DE ATENDIMENTOS
            </h2>
            <div style={{ fontSize: "14px", color: "#555", marginTop: "4px" }}>
              <strong>Cliente:</strong>{" "}
              <span style={{ color: "#802828", fontWeight: "bold" }}>
                {formatarNomeUpper(cliente?.nomeCliente) || formatarNomeUpper(cliente?.nome) || "Cliente"}
                {cpfExibicao}
              </span>
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
            <div
              style={{
                textAlign: "right",
                borderLeft: "2px solid #802828",
                paddingLeft: "12px",
              }}
            >
              <div style={{ fontWeight: "bold", fontSize: "12px" }}>
                Rayane Cristina
              </div>
              <div style={{ fontSize: "10px", color: "#555" }}>
                Rodrigues Pessoa Gomes
              </div>
              <div
                style={{
                  fontSize: "9px",
                  color: "#802828",
                  fontWeight: "bold",
                  marginTop: "2px",
                }}
              >
                ADVOGADA | OAB/PA 27.956 | OAB/MA 23.002-A
              </div>
            </div>

            <button
              onClick={onClose}
              style={{
                background: "none",
                border: "none",
                fontSize: "22px",
                cursor: "pointer",
                color: "#888",
                lineHeight: "1",
              }}
            >
              ✕
            </button>
          </div>
        </div>

        {/* LISTA DE REGISTROS */}
        {carregando ? (
          <div style={{ textAlign: "center", padding: "30px 0", color: "#666" }}>
            Carregando histórico de atendimentos...
          </div>
        ) : atendimentosFinalizados.length === 0 ? (
          <div style={{ textAlign: "center", padding: "30px 0", color: "#777" }}>
            Nenhum atendimento finalizado foi encontrado para este cliente.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            {atendimentosFinalizados.map((item, index) => {
              const nomeAdvogado =
                formatarNomeUpper(item.advogado?.nome) ||
                (typeof item.advogado === "string" ? item.advogado : "Não informado");

              const dataFormatada = item.updatedAt || item.createdAt
                ? new Date(item.updatedAt || item.createdAt).toLocaleString("pt-BR")
                : "Data não registrada";

              return (
                <div
                  key={item._id || item.id || index}
                  style={{
                    border: "1px solid #e0e0e0",
                    borderRadius: "6px",
                    padding: "16px",
                    backgroundColor: "#fafafa",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                  }}
                >
                  {/* Linha Topo: Assunto + Data/Hora + Botão Baixar Ficha */}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      borderBottom: "1px dashed #ccc",
                      paddingBottom: "8px",
                      marginBottom: "12px",
                      flexWrap: "wrap",
                      gap: "8px",
                    }}
                  >
                    <div>
                      <span
                        style={{
                          fontSize: "11px",
                          fontWeight: "bold",
                          color: "#802828",
                          textTransform: "uppercase",
                          display: "block",
                        }}
                      >
                        Assunto
                      </span>
                      <strong style={{ fontSize: "15px", color: "#222" }}>
                        {item.assunto || "Sem assunto"}
                      </strong>
                    </div>

                    <div style={{ textAlign: "right", display: "flex", alignItems: "center", gap: "12px" }}>
                      <div>
                        <span style={{ fontSize: "11px", color: "#777", display: "block" }}>
                          Data e Hora
                        </span>
                        <span style={{ fontSize: "12px", color: "#333", fontWeight: "600" }}>
                          {dataFormatada}
                        </span>
                      </div>

                      <button
                        onClick={() => gerarPDF(item)}
                        style={{
                          backgroundColor: "#fff",
                          color: "#802828",
                          border: "1px solid #802828",
                          padding: "6px 12px",
                          borderRadius: "4px",
                          cursor: "pointer",
                          fontSize: "12px",
                          fontWeight: "bold",
                        }}
                        title="Baixar apenas esta ficha"
                      >
                        📄 Ficha
                      </button>
                    </div>
                  </div>

                  {/* Advogado Responsável */}
                  <div style={{ marginBottom: "10px", fontSize: "12px", color: "#555" }}>
                    <strong>Atendido por:</strong> {nomeAdvogado}
                  </div>

                  {/* Descrição Detalhada */}
                  <div>
                    <span
                      style={{
                        fontSize: "11px",
                        fontWeight: "bold",
                        color: "#555",
                        display: "block",
                        marginBottom: "4px",
                      }}
                    >
                      Descrição do Atendimento:
                    </span>
                    <div
                      style={{
                        padding: "10px",
                        backgroundColor: "#fff",
                        border: "1px solid #eaeaea",
                        borderRadius: "4px",
                        fontSize: "13px",
                        color: "#333",
                        whiteSpace: "pre-wrap",
                        lineHeight: "1.4",
                      }}
                    >
                      {item.descricao || "Sem descrição registrada."}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* RODAPÉ DO MODAL (Com o botão Baixar Prontuário ao lado do Fechar) */}
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: "12px",
            marginTop: "20px",
            paddingTop: "12px",
            borderTop: "1px solid #ddd",
          }}
        >
          {atendimentosFinalizados.length > 0 && (
            <button
              onClick={gerarPDFProntuarioCompleto}
              style={{
                backgroundColor: "#802828",
                color: "#fff",
                border: "none",
                padding: "8px 18px",
                borderRadius: "4px",
                cursor: "pointer",
                fontWeight: "bold",
                fontSize: "14px",
              }}
            >
              📥 Baixar Prontuário Completo
            </button>
          )}

          <button
            onClick={onClose}
            style={{
              backgroundColor: "#6c757d",
              color: "#fff",
              border: "none",
              padding: "8px 18px",
              borderRadius: "4px",
              cursor: "pointer",
              fontWeight: "bold",
              fontSize: "14px",
            }}
          >
            Fechar Prontuário
          </button>
        </div>
      </div>
    </div>
  );
};

export default ModalProntuarioCliente;